import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CircleChecked } from "../../assets/icons/CircleChecked";
import { useDebounce } from "../../hooks/useDebounce";
import { Button } from "../Button";
import { IOption } from "../SelectDropdown";
import { Tag } from "../Tag";
import { TextField } from "../TextField";
import {
  CapNote,
  Footer,
  HintText,
  ModalContent,
  ModalTitle,
  ResultLabel,
  ResultRow,
  ResultsList,
  SearchWrapper,
  SelectedChipsContainer,
} from "./SnomedSelect.styles";

const MIN_SEARCH_LENGTH = 3;
const SEARCH_LIMIT = 50;

type SnomedSelectModalProps = {
  title: string;
  searchPlaceholder: string;
  searchOptions: (search: string) => Promise<IOption[]>;
  queryKey: string;
  value: IOption[];
  onChange: (selected: IOption[]) => void;
  onClose: () => void;
};

export function SnomedSelectModal({
  title,
  searchPlaceholder,
  searchOptions,
  queryKey,
  value,
  onChange,
  onClose,
}: SnomedSelectModalProps) {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);

  const canSearch = debouncedSearch.length >= MIN_SEARCH_LENGTH;

  const { data: results = [], isFetching } = useQuery<IOption[]>({
    queryKey: [queryKey, "search", debouncedSearch],
    queryFn: () => searchOptions(debouncedSearch),
    enabled: canSearch,
    staleTime: 5 * 60 * 1000,
  });

  const isSelected = (optionValue: string) =>
    value.some((option) => option.value === optionValue);

  const toggleOption = (option: IOption) => {
    if (isSelected(option.value)) {
      onChange(value.filter((selected) => selected.value !== option.value));
    } else {
      onChange([...value, option]);
    }
  };

  const renderResults = () => {
    if (!canSearch) {
      return null;
    }

    if (isFetching) {
      return <HintText>{t("common_searching")}</HintText>;
    }

    if (results.length === 0) {
      return <HintText>{t("common_noResults")}</HintText>;
    }

    return (
      <>
        <ResultsList>
          {results.map((option) => {
            const selected = isSelected(option.value);

            return (
              <ResultRow
                key={option.key}
                type="button"
                selected={selected}
                onClick={() => toggleOption(option)}
              >
                <ResultLabel>{option.label}</ResultLabel>
                {selected && <CircleChecked />}
              </ResultRow>
            );
          })}
        </ResultsList>

        {results.length >= SEARCH_LIMIT && (
          <CapNote>
            {t("common_showingFirstMatches", { count: SEARCH_LIMIT })}
          </CapNote>
        )}
      </>
    );
  };

  return (
    <ModalContent>
      <ModalTitle>{title}</ModalTitle>

      {value.length > 0 && (
        <SelectedChipsContainer>
          {value.map((option) => (
            <Tag
              key={option.key}
              closeButtonLocation="right"
              onCloseClick={() => toggleOption(option)}
            >
              {option.label}
            </Tag>
          ))}
        </SelectedChipsContainer>
      )}

      <SearchWrapper>
        <TextField
          autofocus
          label=""
          name="snomed-search"
          placeholder={searchPlaceholder}
          value={search}
          backgroudColor="white"
          enterKeyHint="search"
          onChange={(event) => setSearch(event.target.value)}
        />
      </SearchWrapper>

      {renderResults()}

      <Footer>
        <Button type="button" label={t("common_done")} onClick={onClose} />
      </Footer>
    </ModalContent>
  );
}
